import { useCallback, useEffect, useRef, useState, useId } from 'react';
import { Navigation, Lock, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useGPSCapture } from './useGPSCapture';
import { gpsApi, gpsError } from './gpsApi';
import './gps.css';

export const PinDistance = ({ courseId, hole }) => {
  const id = useId().replace(/:/g, '');
  const gps = useGPSCapture();
  const cancelCapture = gps.cancel;
  const [access, setAccess] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const generation = useRef(0);
  const lock = useRef(false);
  const refreshTimer = useRef(null);
  const pauseTimer = useRef(null);
  const activeUntil = useRef(0);
  const clearTimers = useCallback(() => { clearTimeout(refreshTimer.current); clearTimeout(pauseTimer.current); }, []);
  const stopSession = useCallback((message = '') => { activeUntil.current = 0; clearTimers(); setSessionActive(false); if (message) setError(message); }, [clearTimers]);
  useEffect(() => {
    const tracker = generation;
    const run = ++generation.current;
    stopSession(); cancelCapture(); lock.current = false; setWorking(false); setAccess(null); setResult(null); setError('');
    if (courseId) gpsApi.status(courseId).then(data => { if (run === generation.current) setAccess(data); })
      .catch(() => { if (run === generation.current) setAccess({ available: false }); });
    return () => { tracker.current++; stopSession(); cancelCapture(); };
  }, [courseId, hole, cancelCapture, stopSession]);
  useEffect(() => {
    const hidden = () => { if (document.hidden) stopSession(); };
    document.addEventListener('visibilitychange', hidden);
    return () => document.removeEventListener('visibilitychange', hidden);
  }, [stopSession]);
  const measure = useCallback(async (run) => {
    if (lock.current) return;
    lock.current = true;
    setWorking(true); setError(''); setResult(null);
    try {
      const readings = await gps.collect();
      const measured = await gpsApi.send(courseId, 'distance', { hole: Number(hole), readings });
      if (run === generation.current) setResult(measured);
    } catch (err) {
      if (run === generation.current && err.name !== 'AbortError') setError(gpsError(err));
    } finally {
      if (run === generation.current) { setWorking(false); lock.current = false; }
      if (run === generation.current && activeUntil.current > Date.now() && !document.hidden) refreshTimer.current = setTimeout(() => measure(run), 10000);
    }
  }, [courseId, hole, gps]);
  const activate = () => {
    if (lock.current) return;
    clearTimers(); const run = ++generation.current; activeUntil.current = Date.now() + 30000; setSessionActive(true);
    pauseTimer.current = setTimeout(() => { activeUntil.current = 0; clearTimeout(refreshTimer.current); setSessionActive(false); }, 30000);
    measure(run);
  };
  const pinAvailable = !Array.isArray(access?.pin_holes) || access.pin_holes.includes(Number(hole));
  return <section className="gps-distance" data-testid={`pin-distance-${id}`} onTouchStart={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
    {!access?.available ? <div className="gps-coming" data-testid={`pin-coming-soon-${id}`}><Lock size={15} /> Pin distance · Coming soon</div> : <>
      <p className="gps-caption" data-testid={`pin-pilot-label-${id}`}>LIVE PIN · HOLE {hole}</p>
      {!pinAvailable ? <p className="gps-caption" data-testid={`pin-unpublished-${id}`}>Pin location has not been published for this hole.</p> : <Button type="button" className="gps-primary" onClick={activate} disabled={working || !access.configured} data-testid={`golfer-tap-to-refresh-pin-btn-${id}`}>
        <Navigation size={16} /> {working ? (gps.busy ? `Refreshing · ${gps.progress}/3` : 'Updating…') : result ? 'Pin location · Refresh' : 'Pin location'}
      </Button>
      }
      {!access.configured && <p className="gps-caption" data-testid={`pin-unconfigured-${id}`}>Course GPS pilot not configured.</p>}
      {working && <Button type="button" variant="ghost" onClick={() => { generation.current++; stopSession(); gps.cancel(); lock.current = false; setWorking(false); }} data-testid={`pin-cancel-${id}`}><X size={14} /> Stop</Button>}
      {result && <div aria-live="polite" data-testid={`pin-result-${id}`}><strong className="gps-yardage" data-testid={`golfer-live-pin-yardage-display-${id}`}>{result.distance_yards}<small> YDS TO PIN</small></strong></div>}
      <p className="gps-caption" data-testid={`golfer-battery-save-status-pill-${id}`}>{sessionActive ? 'Short live refresh · pauses after 30 sec' : result ? 'Paused to save battery · tap to refresh' : 'Updates only when requested'}</p>
      {working && !error && !gps.error && <p role="status" className="gps-caption" data-testid={`golfer-pin-refresh-radar-indicator-${id}`}>{gps.message}</p>}
      {(error || gps.error) && <p role="alert" className="gps-error" data-testid={`pin-error-${id}`}>{error || gps.error}</p>}
    </>}
  </section>;
};